import React, { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { value: 'payment_cleared', label: 'Payment Cleared' },
  { value: 'not_needed', label: 'Not Needed' },
  { value: 'changes_in_value', label: 'Changes in Value' },
];

export default function RemoveTorDialog({ open, onClose, tor, onSuccess }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [reason, setReason] = useState('');
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    if (!reason) { toast.error('Please select a reason'); return; }
    setRemoving(true);
    try {
      await axios.delete(`${API}/tors/${tor.tor_id}`, {
        data: { reason: REASONS.find(r => r.value === reason)?.label || reason },
        withCredentials: true
      });
      toast.success(tor.linked_user_id && tor.status === 'accepted'
        ? 'Removal request sent for approval'
        : 'Removed successfully');
      setReason('');
      onClose();
      onSuccess();
    } catch (err) {
      toast.error('Failed to remove');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setReason(''); onClose(); } }}>
      <DialogContent className={`rounded-md max-w-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`} data-testid="remove-tor-dialog">
        <DialogHeader>
          <DialogTitle className={`font-outfit text-lg font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
            Remove {tor?.tor_type === 'debtor' ? 'Debtor' : 'Creditor'}
          </DialogTitle>
          <DialogDescription className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            Remove {tor?.name}? {tor?.linked_user_id && tor?.status === 'accepted' ? 'The linked user must approve this removal.' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3">
          <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Reason *</label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className={`h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`} data-testid="remove-reason-select">
              <SelectValue placeholder="Select reason" />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-zinc-900 border-zinc-800' : ''}>
              {REASONS.map(r => (
                <SelectItem key={r.value} value={r.value} className="font-ibm text-sm">{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={() => { setReason(''); onClose(); }}
            className={`flex-1 h-10 font-ibm text-sm font-medium rounded-sm border transition-all ${isDark ? 'border-zinc-700 text-zinc-400 hover:bg-zinc-800' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
            data-testid="cancel-remove-tor"
          >
            Cancel
          </button>
          <button
            onClick={handleRemove}
            disabled={removing}
            className="flex-1 h-10 bg-red-500 text-white font-ibm text-sm font-semibold rounded-sm hover:bg-red-600 active:scale-[0.98] transition-all disabled:opacity-50"
            data-testid="confirm-remove-tor"
          >
            {removing ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
